// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** A manifest's reference families, whichever of the two fields it declares. */
export function manifestReferencePrefixes(manifest) {
    if (manifest.reference_prefixes && manifest.reference_prefixes.length > 0) {
        return [...manifest.reference_prefixes];
    }
    return manifest.reference_prefix ? [manifest.reference_prefix] : [];
}
/** A manifest's evidence packs, whichever of the two fields it declares. */
export function manifestEvidencePacks(manifest) {
    if (manifest.evidence_packs)
        return [...manifest.evidence_packs];
    return manifest.evidence_pack ? [manifest.evidence_pack] : [];
}
/** A manifest's kind; absent is `industry`. */
export function manifestKind(manifest) {
    return manifest.kind ?? 'industry';
}
/** The first line of every file a package generates. */
export const SAMPLE_DATA_SYNTHETIC_FILE_STAMP = 'SYNTHETIC — SAMPLE DATA';
/**
 * Sprint 63.37.0, H1 — the synthetic stamp in each language a sample output
 * prints in. Every PDF and DOCX output of a form instance that carries the
 * sample-data marker prints this in the header of every page and in its
 * document properties. English is {@link SAMPLE_DATA_SYNTHETIC_FILE_STAMP}
 * itself, so the generated files and the printed forms say the same words.
 */
export const SAMPLE_DATA_SYNTHETIC_FILE_STAMPS = {
    en: SAMPLE_DATA_SYNTHETIC_FILE_STAMP,
    vi: 'DỮ LIỆU MẪU — TỔNG HỢP',
};
/**
 * The synthetic stamp for an output locale (`vi`, `vi-VN`, `en-GB`, …): the
 * Vietnamese stamp for a Vietnamese output, the English stamp for every other.
 */
export function sampleDataSyntheticStamp(locale) {
    const lang = String(locale ?? '')
        .split('-')[0]
        .toLowerCase();
    return lang === 'vi'
        ? SAMPLE_DATA_SYNTHETIC_FILE_STAMPS.vi
        : SAMPLE_DATA_SYNTHETIC_FILE_STAMPS.en;
}
/**
 * Sprint 63.37.0, A5 — the computed fields a record may ask for, each computed
 * by the owning module's own function. The directive sits on the field named
 * here; the row receives what the entry writes.
 *
 * | Name | Placed on | Writes | Function |
 * |---|---|---|---|
 * | `rights_request_deadlines` | `sla_due_at` | `sla_due_at` (+ `fulfillment_due_at` when pack-backed) | `caseFulfilmentDeadlineFields` |
 * | `deletion_proof_chain` | `event_hash` | `event_hash`, `previous_hash`, `hash_inputs.previous_hash` | `computeProofEventHash` |
 * | `case_audit_checksum_chain` | `checksum` | `checksum`, `sequence_number` | `AuditService.computeChecksum` |
 * | `access_log_proofs` | `access_proof` | nothing on the row; the row is anchored after the write | `ProofService.batchAnchor` |
 * | `sub_processor_pair_key` | `pair_key` | `pair_key`, one live link per unordered vendor pair | `subProcessorPairKey` |
 *
 * `deletion_proof_chain` also writes `sequence` on the row and into `hash_inputs`
 * (merge of `main`, Sprint 63.29.0 #486: links that share an instant are ordered
 * by their sequence, as `ProofRecordService` writes it).
 */
export const SAMPLE_PACKAGE_COMPUTED_FIELDS = [
    'rights_request_deadlines',
    'deletion_proof_chain',
    'case_audit_checksum_chain',
    'access_log_proofs',
    'sub_processor_pair_key',
];
/**
 * Gate E12.5 P3-5 — the error code a reinstall returns when it fails after the
 * previous install was removed. The error's details carry
 * {@link SampleDataPackageReinstallFailedDetails}.
 */
export const SAMPLE_PACKAGE_REINSTALL_FAILED_CODE = 'SAMPLE_PACKAGE_REINSTALL_FAILED';
/**
 * The `register` a kept export names when the per-owner-type `legal_hold` file
 * policy (Platform Files settings) covers its file; `register_entry` is the
 * owner type. The layer the platform's hard delete passes only with
 * `platform:files:legal_hold:override` and an explicit flag.
 */
export const SAMPLE_DATA_KEPT_BY_LEGAL_HOLD_POLICY = 'legal_hold_policy';
//# sourceMappingURL=sample-data-package.js.map